import axios from 'axios';

// API Base URL - always use relative paths so it works on any domain
const API_BASE = '/api';

// Token management utilities
const TOKEN_KEY = 'auth_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const removeToken = () => localStorage.removeItem(TOKEN_KEY);

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    // Send the httpOnly `session_token` cookie alongside Bearer header.
    // Defense in depth: even if a future XSS leaks the Bearer token, the
    // session cookie can be invalidated server-side without compromising
    // every authenticated route.
    withCredentials: true,
});

// ─────────────────────────────────────────────────────────────────────
// Phase 3 — GET request deduplication + micro-cache
// ─────────────────────────────────────────────────────────────────────
// Two problems this fixes (measured in audit):
//   1. 11 places call GET /auth/me → many concurrent duplicates on mount.
//   2. React StrictMode double-invokes effects → doubled API traffic in dev.
// Strategy:
//   - In-flight promise cache: while a GET is pending for a given URL,
//     any concurrent request to the same URL returns the same promise.
//   - Short TTL cache (default 2 s) for a small allow-list of endpoints
//     that are stable within a page-load window. Zero TTL = no caching.
//   - Any non-GET request (POST/PUT/PATCH/DELETE) invalidates all cached
//     entries whose URL shares the first path segment (safe & broad).
// ─────────────────────────────────────────────────────────────────────
const _inFlight = new Map();     // key: METHOD+URL → Promise
const _microCache = new Map();   // key: URL → { data, expiresAt }

// TTL (ms) per URL prefix. Tuned for endpoints that don't change within
// a single navigation. Extend only after checking real usage.
const _CACHE_TTL = {
    // Session identity (rarely changes during a session)
    '/auth/me': 10000,
    '/internal/check-access': 10000,
    // Notification counters (polled by badge widgets, can tolerate 3 s lag)
    '/notifications/counts': 3000,
    '/messaging/unread-count': 3000,
    '/reseau/notifications/unread-count': 3000,
    // Mandates list — heavy query, reloaded by many components on mount
    '/mandates-v2/': 5000,
    // AI briefing — expensive LLM call, cache for 30 s on client side too
    '/cockpit-ai/briefing': 30000,
    // Internal super-admin dashboards — read-only KPIs, stable across taps
    '/internal/dashboard': 15000,
    '/internal/demo-requests': 15000,
    '/internal/v1/activation': 15000,
    '/internal/v1/pipeline-mrr': 15000,
    '/internal/v1/cohorts': 15000,
    '/internal/v1/nps/scores': 15000,
    '/internal/v1/churn-signals': 15000,
    '/internal/v1/health-scores': 15000,
    '/internal/v1/feature-retention-correlation': 15000,
    '/internal/v1/engagement': 15000,
    '/internal/v1/feature-adoption': 15000,
    '/internal/monitoring/latency': 5000,
    // Groups (director view) — refreshed rarely
    '/groups/me': 10000,
    // NPS eligibility — user-scoped, changes daily at most
    '/nps/eligibility': 30000,
};

function _ttlFor(url) {
    for (const prefix in _CACHE_TTL) {
        if (url === prefix || url.startsWith(prefix + '?')) return _CACHE_TTL[prefix];
    }
    return 0;
}

function _cacheKey(config) {
    const params = config.params ? JSON.stringify(config.params) : '';
    return `${(config.method || 'get').toUpperCase()} ${config.url}${params}`;
}

// Public helper: manually invalidate the cache (e.g. after a mutation).
export const invalidateApiCache = (urlPrefix) => {
    if (!urlPrefix) { _microCache.clear(); return; }
    for (const key of _microCache.keys()) {
        if (key.startsWith(urlPrefix)) _microCache.delete(key);
    }
};

// Request interceptor: dedupe + micro-cache for GET; auto-invalidate on mutations.
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// We wrap the axios instance in a proxied get() that adds dedupe + cache.
const _rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
    // Skip cache/dedupe when caller opts out
    if (config.__nocache) return _rawGet(url, config);

    const cfg = { ...config, method: 'get', url };
    const key = _cacheKey(cfg);
    const ttl = _ttlFor(url);

    // Serve fresh cache hit
    if (ttl > 0) {
        const hit = _microCache.get(url);
        if (hit && hit.expiresAt > Date.now()) {
            return Promise.resolve({ data: hit.data, status: 200, cached: true });
        }
    }

    // Coalesce concurrent in-flight requests
    const existing = _inFlight.get(key);
    if (existing) return existing;

    const p = _rawGet(url, config).then(
        (resp) => {
            if (ttl > 0) {
                _microCache.set(url, { data: resp.data, expiresAt: Date.now() + ttl });
            }
            _inFlight.delete(key);
            return resp;
        },
        (err) => {
            _inFlight.delete(key);
            throw err;
        }
    );
    _inFlight.set(key, p);
    return p;
};

// Mutations invalidate related cache entries (first path segment).
['post', 'put', 'patch', 'delete'].forEach((verb) => {
    const raw = api[verb].bind(api);
    api[verb] = (url, ...rest) => {
        const first = (url || '').split('?')[0].split('/').filter(Boolean)[0];
        if (first) invalidateApiCache('/' + first);
        return raw(url, ...rest);
    };
});

// Response interceptor: handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        // 401 = invalid token | 403 + "Not authenticated" = HTTPBearer no-token
        // (FastAPI HTTPBearer renvoie 403 quand le header Authorization est absent)
        const isAuthError = status === 401 || (status === 403 && detail === 'Not authenticated');

        if (isAuthError) {
            const isAuthPage = window.location.pathname.includes('/login') || 
                              window.location.pathname.includes('/register') ||
                              window.location.pathname.includes('/onboarding') ||
                              window.location.pathname.includes('/verify-email') ||
                              window.location.pathname.includes('/confirm-email') ||
                              window.location.pathname.includes('/reset-password') ||
                              window.location.pathname.includes('/forgot-password') ||
                              window.location.pathname.includes('/support') ||
                              window.location.pathname.includes('/faq') ||
                              window.location.pathname.startsWith('/reseau') ||
                              window.location.pathname.startsWith('/p/') ||
                              window.location.pathname.startsWith('/avis/') ||
                              window.location.hash?.includes('session_id');
            
            if (!isAuthPage) {
                removeToken();
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (userData, agencyData) => 
        api.post('/auth/register', { 
            ...userData, 
            name: agencyData.name,
            siret: agencyData.siret,
            address: agencyData.address,
            city: agencyData.city,
            postal_code: agencyData.postal_code,
            agency_email: agencyData.email,
            agency_phone: agencyData.phone,
            logo_url: agencyData.logo_url
        }),
    login: (credentials) => 
        api.post('/auth/login', credentials),
    me: () => 
        api.get('/auth/me'),
    logout: () =>
        api.post('/auth/logout'),
    session: (sessionId) =>
        api.post('/auth/session', { session_id: sessionId }),
    onboarding: (data) =>
        api.post('/auth/onboarding', data),
    forgotPassword: (email) => 
        api.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) => 
        api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Agency API
export const agencyAPI = {
    getMyAgency: () => 
        api.get('/agencies/me'),
    updateAgency: (data) => 
        api.put('/agencies/me', data),
};

// Users API
export const usersAPI = {
    getTeam: () => 
        api.get('/users/team'),
    inviteAgent: (data) => 
        api.post('/users/invite-agent', data),
    createAgentDirect: (data) => 
        api.post('/users/create-agent-direct', data).then(res => res.data),
    acceptInvite: (token, password) => 
        api.post('/users/accept-invite', { token, password }),
    toggleUserActive: (userId) => 
        api.put(`/users/${userId}/toggle-active`),
};

// Properties API
export const propertiesAPI = {
    create: (data) => 
        api.post('/properties/', data),
    getAll: () => 
        api.get('/properties/'),
    getById: (id) => 
        api.get(`/properties/${id}`),
};

// Mandates API
export const mandatesAPI = {
    create: (data) => 
        api.post('/mandates/', data),
    getAll: (status = null) => 
        api.get('/mandates/', { params: { status } }),
    getById: (id) => 
        api.get(`/mandates/${id}`),
    update: (id, data) => 
        api.put(`/mandates/${id}`, data),
    sendForSignature: (id) => 
        api.post(`/mandates/${id}/send-for-signature`),
    getTimeline: (id) => 
        api.get(`/mandates/${id}/timeline`),
    createAvenant: (id, newPrice, reason) => 
        api.post(`/mandates/${id}/avenant`, { new_price: newPrice, reason }),
};

// Signatures API
export const signaturesAPI = {
    getEnvelope: (id) => 
        api.get(`/signatures/envelope/${id}`),
    sign: (envelopeId) => 
        api.post(`/signatures/envelope/${envelopeId}/sign`),
    getPending: () => 
        api.get('/signatures/pending'),
};

// Billing API
export const billingAPI = {
    // Legacy (kept for /billing checkout flow)
    getSubscription: () =>
        api.get('/billing/subscription'),
    getUsage: () =>
        api.get('/billing/usage'),
    createCheckout: () =>
        api.post('/billing/checkout'),
    getCheckoutStatus: (sessionId) =>
        api.get(`/billing/checkout/status/${sessionId}`),
    // New routers/billing.py endpoints
    getMe: () =>
        api.get('/billing/me'),
    getTransactions: (limit = 50) =>
        api.get('/billing/transactions', { params: { limit } }),
    openPortal: () =>
        api.post('/billing/portal'),
};

// Notifications API
export const notificationsAPI = {
    getAll: (unreadOnly = false) => 
        api.get('/notifications/', { params: { unread_only: unreadOnly } }),
    markRead: (id) => 
        api.put(`/notifications/${id}/read`),
    markAllRead: () => 
        api.put('/notifications/read-all'),
};

// Ratings API
export const ratingsAPI = {
    create: (data) => 
        api.post('/ratings/', data),
    getAgentRatings: (agentId) => 
        api.get(`/ratings/agent/${agentId}`),
    respond: (ratingId, response) => 
        api.put(`/ratings/${ratingId}/respond`, { response }),
};

// Reminders API
export const remindersAPI = {
    getAll: (completed = null) => 
        api.get('/reminders/', { params: { completed } }),
    create: (data) => 
        api.post('/reminders/', data),
    complete: (id) => 
        api.put(`/reminders/${id}/complete`),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => 
        api.get('/dashboard/stats'),
};

// Export API
export const exportAPI = {
    mandates: () => 
        api.get('/export/mandates'),
};

// Legal API
export const legalAPI = {
    getCGU: () => 
        api.get('/legal/cgu'),
    getPrivacy: () => 
        api.get('/legal/privacy'),
    getCookies: () => 
        api.get('/legal/cookies'),
};

// Seed API
export const seedAPI = {
    seed: () => 
        api.post('/seed'),
};

// AI Assistant API
export const aiAPI = {
    chat: (message, sessionId = null, mandateId = null, isUrgent = false) =>
        api.post('/ai/chat', { 
            message, 
            session_id: sessionId, 
            mandate_id: mandateId,
            is_urgent: isUrgent
        }),
    getHistory: (sessionId) =>
        api.get(`/ai/history/${sessionId}`),
    getTickets: (status = null) =>
        api.get('/ai/tickets', { params: { status } }),
    resolveTicket: (ticketId, resolution) =>
        api.put(`/ai/tickets/${ticketId}/resolve`, { resolution }),
    getKPIs: () =>
        api.get('/ai/kpis'),
};

// Messaging API
export const messagingAPI = {
    getConversations: (page = 1, type = null) =>
        api.get('/messaging/conversations', { params: { page, type } }),
    createConversation: (participantId, subject, initialMessage, conversationType = 'direct', participantIds = null) =>
        api.post('/messaging/conversations', {
            participant_id: participantId,
            participant_ids: participantIds,
            subject,
            initial_message: initialMessage,
            conversation_type: conversationType,
        }),
    createAgencyGroup: () =>
        api.post('/messaging/agency-group'),
    getMessages: (conversationId, page = 1) =>
        api.get(`/messaging/conversations/${conversationId}`, { params: { page } }),
    sendMessage: (conversationId, content, messageType = 'text', guidedCategory = null) =>
        api.post(`/messaging/conversations/${conversationId}/messages`, {
            content,
            message_type: messageType,
            guided_category: guidedCategory,
        }),
    getUnreadCount: () =>
        api.get('/messaging/unread-count'),
    getContacts: (search = null, type = null) =>
        api.get('/messaging/contacts', { params: { search, type } }),
    getGuidedTemplates: () =>
        api.get('/messaging/guided-templates'),
    extractReminders: (conversationId) =>
        api.post('/messaging/extract-reminders', { conversation_id: conversationId }),
    getAiReminders: (completed = null) =>
        api.get('/messaging/ai-reminders', { params: { completed } }),
    getDashboardActions: () =>
        api.get('/messaging/ai-actions/dashboard'),
    completeAiReminder: (reminderId) =>
        api.put(`/messaging/ai-reminders/${reminderId}/complete`),
    deleteAiReminder: (reminderId) =>
        api.delete(`/messaging/ai-reminders/${reminderId}`),
};

// Visit Reports API
export const visitsAPI = {
    generateLink: (mandateId, propertyAddress) =>
        api.post('/visits/generate-link', { mandate_id: mandateId, property_address: propertyAddress }),
    getForm: (token) =>
        api.get(`/visits/form/${token}`),
    submitReport: (token, data) =>
        api.post(`/visits/form/${token}/submit`, data),
    getLinks: (mandateId) =>
        api.get(`/visits/mandate/${mandateId}/links`),
    getReports: (mandateId) =>
        api.get(`/visits/mandate/${mandateId}/reports`),
    sendFormEmail: (payload) =>
        api.post('/visits/send-form-email', payload),
    manualReport: (mandateId, data) =>
        api.post(`/visits/mandate/${mandateId}/manual-report`, data),
};

// Bons de visite acquéreur (anonymisés)
export const bonVisiteAPI = {
    create: (payload) => api.post('/visit-slips', payload),
    listByMandate: (mandateId) => api.get(`/visit-slips/mandate/${mandateId}`),
    remove: (slipId) => api.delete(`/visit-slips/${slipId}`),
    getForSigning: (token) => api.get(`/visit-slips/sign/${token}`),
    sign: (token, payload) => api.post(`/visit-slips/sign/${token}`, payload),
    pdfUrl: (slipId) => `/api/visit-slips/${slipId}/pdf`,
};

export default api;
